import { customGameTypesFilter } from 'features/creativeFunctionality/data';
import additionalCrashGames from 'features/creativeFunctionality/data/additionalCrashGames';
import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { normalizeProviderValue, uniqueBy, isEqualByKeys } from '../shared';

const normalizeGameType = type => (type ?? '').toString().replace(/_/g, '-');
const getGameKey = ({ type, provider, value }) => `${normalizeGameType(type)}:${normalizeProviderValue(provider)}:${value}`;
const EL_CASINO_GAME_IDS = new Set(['22', '24', '25', '26', '27', '28', '60', '61', '67', '517', '518']);
const MANUAL_CRASH_GAME_IDS = {
  jacktop: new Set(['154912', '140511', '168713']),
  tvbet: new Set(['219']),
  bgaming: new Set(['Aviamasters']),
};
const PRAGMATIC_RGS_PROVIDER = 'pragmatic-rgs';
const CRASH_GAME_TYPE = 'crash-games';
const LIVE_CASINO_GAME_TYPE = 'live-casino';
const VIRTUAL_GAMES_TYPE = 'virtual-games';

const normalizeTabs = tabs => (Array.isArray(tabs) ? tabs.map(tab => tab?.toString().toLowerCase()) : []);
const normalizeGameValue = value => (value ?? '').toString();
const getProviderDisplayName = ({ provider, type, fallbackName }) => {
  if (provider === PRAGMATIC_RGS_PROVIDER && type === 'slots') return 'Pragmatic RGS';
  return fallbackName;
};

// Локальный слой только для персонализации: выводит провайдера/категорию отображения без изменения общих конвертеров казино
const getEffectiveProvider = (game, type = game.type) => {
  const provider = normalizeProviderValue(game.provider);
  const routeProvider = normalizeProviderValue(game.routeProvider);
  const gameValue = normalizeGameValue(game.value);

  if (type === LIVE_CASINO_GAME_TYPE) {
    if (provider === 'pragmatic') return 'pragmaticlive';
    if (provider === 'tvbet' && EL_CASINO_GAME_IDS.has(gameValue)) return 'elcasino';
  }

  if (
    (routeProvider === 'winfinity' || provider === 'gambit' || provider === 'thehood' || provider === 'the-hood') &&
    (provider === 'gambit' || provider === 'thehood' || provider === 'the-hood')
  ) {
    return 'the-hood';
  }

  return provider;
};

const hasEffectiveType = (game, type) => {
  const normalizedType = normalizeGameType(game.type);
  const provider = normalizeProviderValue(game.provider);
  const routeProvider = normalizeProviderValue(game.routeProvider);

  if (type === 'slots' && (provider === 'universalrace' || routeProvider === 'universalrace')) {
    return false;
  }

  if (normalizedType === type) return true;

  const tabs = normalizeTabs(game.tab);

  if (type === CRASH_GAME_TYPE) {
    if (provider === PRAGMATIC_RGS_PROVIDER || routeProvider === PRAGMATIC_RGS_PROVIDER) return false;
    if (tabs.includes('crash')) return true;
    if ((routeProvider === 'outcome' || provider === 'inout') && provider === 'inout') return true;
    if (MANUAL_CRASH_GAME_IDS[routeProvider]?.has(normalizeGameValue(game.value))) return true;
    return false;
  }

  if (type === VIRTUAL_GAMES_TYPE) {
    return provider === 'universalrace';
  }

  return false;
};

const toDisplayGame = (game, type) => {
  const provider = getEffectiveProvider(game, type);

  return {
    ...game,
    type,
    provider,
    key: getGameKey({ type, provider, value: game.value }),
  };
};

const normalizeGame = game => {
  const type = normalizeGameType(game.type);
  const provider = getEffectiveProvider({ ...game, type }, type);

  return {
    ...game,
    type,
    provider,
    key: game.key ?? getGameKey({ type, provider, value: game.value }),
  };
};

const getProviderKey = ({ type, provider, value }) => `${type}:${normalizeProviderValue(provider ?? value)}`;

export const useCustomGamesConverter = () => {
  const { gameList: casinoGames } = useSelector(state => state.casino, shallowEqual);
  const {
    games: customGames,
    previousGames,
    providers: customProviders,
  } = useSelector(state => state.creativeFunctionality, shallowEqual);

  const [selectedGameType, selectGameType] = useState(customGameTypesFilter[0]);
  const [selectedProvider, selectProvider] = useState(null);
  const [orderedGames, changeOrderedGames] = useState([]);
  const [searchString, setSearchString] = useState('');
  const [searchResult, setSearchResult] = useState('');

  const providerMetaByTypeAndSlug = useMemo(() => {
    const map = new Map();
    (customProviders?.items ?? []).forEach(p => {
      const key = getProviderKey(p);
      if (!map.has(key)) {
        map.set(key, {
          apiValue: p.apiValue ?? p.value,
          name: p.name,
        });
      }
    });
    return map;
  }, [customProviders]);

  const convertedCasinoGames = useMemo(
    () =>
      casinoGames?.map(g =>
        normalizeGame({
          name: g.gameName,
          value: g.gameId ?? g.id,
          type: g.type,
          provider: g.provider,
          routeProvider: g.routeProvider,
          tab: g.tab,
          isActive: false,
          position: 0,
          imgUrl: g.imageUrl,
        }),
      ) ?? [],
    [casinoGames],
  );

  const normalizedCustomGames = useMemo(() => (customGames ?? []).map(normalizeGame), [customGames]);
  const normalizedCrashSupplementGames = useMemo(() => additionalCrashGames.map(normalizeGame), []);
  const normalizedPreviousGames = useMemo(() => (previousGames ?? []).map(normalizeGame), [previousGames]);

  const handleSendSearchResult = () => {
    setSearchResult(searchString);
  };

  const allGames = useMemo(() => {
    if (!convertedCasinoGames.length) return uniqueBy([...normalizedCrashSupplementGames, ...normalizedCustomGames], 'key');

    const customGamesMap = new Map(normalizedCustomGames.map(g => [g.key, g]));
    const mappedCasinoGames = convertedCasinoGames.map(g => customGamesMap.get(g.key) ?? g);

    return uniqueBy([...mappedCasinoGames, ...normalizedCrashSupplementGames, ...normalizedCustomGames], 'key');
  }, [convertedCasinoGames, normalizedCrashSupplementGames, normalizedCustomGames]);

  const total = allGames.length;

  const gamesBySelectedType = useMemo(() => {
    if (!selectedGameType) return [];

    return uniqueBy(
      allGames
        .filter(game => hasEffectiveType(game, selectedGameType.value))
        .map(game => toDisplayGame(game, selectedGameType.value)),
      'key',
    );
  }, [allGames, selectedGameType]);

  const providers = useMemo(() => {
    const mapped = gamesBySelectedType.map(g => {
      const providerKey = getProviderKey(g);
      const providerMeta = providerMetaByTypeAndSlug.get(providerKey);

      return {
        key: providerKey,
        value: g.provider,
        apiValue: providerMeta?.apiValue ?? g.provider,
        name: getProviderDisplayName({
          provider: g.provider,
          type: g.type,
          fallbackName: providerMeta?.name ?? (g.provider ? g.provider[0].toUpperCase() + g.provider.slice(1) : ''),
        }),
        type: g.type,
        isActive: selectedProvider ? selectedProvider.key === providerKey : false,
      };
    });

    return uniqueBy(mapped, 'key');
  }, [gamesBySelectedType, selectedProvider, providerMetaByTypeAndSlug]);

  const disabledProviders = useMemo(() => {
    return (customProviders?.items ?? [])
      .filter(p => p.disabled || p.isActive === false)
      .map(p => ({
        ...p,
        key: getProviderKey(p),
        value: normalizeProviderValue(p.value),
        apiValue: p.apiValue ?? p.value,
      }));
  }, [customProviders]);

  const availableProviders = useMemo(() => {
    const disabledSet = new Set(disabledProviders.map(p => p.key));
    return providers.filter(p => !disabledSet.has(p.key));
  }, [providers, disabledProviders]);

  const filteredProviders = useMemo(() => {
    return uniqueBy(availableProviders.filter(p => selectedGameType && p.type === selectedGameType.value), 'key');
  }, [availableProviders, selectedGameType]);

  const filteredGames = useMemo(() => {
    const searchLower = searchResult.toLowerCase();
    const orderedGamesMap = new Map(orderedGames.map(g => [g.key, g]));

    return uniqueBy(
      gamesBySelectedType
        .filter(
          g =>
            selectedGameType &&
            g.type === selectedGameType.value &&
            selectedProvider &&
            getProviderKey(g) === selectedProvider.key &&
            g.name.toLowerCase().includes(searchLower),
        )
        .map(g => orderedGamesMap.get(g.key) ?? g)
        .sort((a, b) => {
          if (a.position === 0 && b.position !== 0) return 1;
          if (b.position === 0 && a.position !== 0) return -1;
          return a.position - b.position;
        }),
      'key',
    );
  }, [gamesBySelectedType, selectedProvider, selectedGameType, orderedGames, searchResult]);

  const allPositionedGames = useMemo(
    () => uniqueBy([...normalizedCustomGames, ...orderedGames], 'key').filter(g => g.position > 0),
    [normalizedCustomGames, orderedGames],
  );

  const gameTypes = useMemo(
    () =>
      customGameTypesFilter.map(gt => ({
        ...gt,
        isActive: selectedGameType && gt.value === selectedGameType.value,
      })),
    [selectedGameType],
  );

  const hasGamesBeenChanged = useMemo(
    () => isEqualByKeys(normalizedPreviousGames, allPositionedGames, ['key', 'position', 'isActive']),
    [allPositionedGames, normalizedPreviousGames],
  );

  useEffect(() => {
    if (filteredProviders.length === 0) {
      if (selectedProvider) selectProvider(null);
      return;
    }

    const hasSelected = selectedProvider && filteredProviders.some(p => p.key === selectedProvider.key);
    if (!hasSelected) selectProvider(filteredProviders[0]);
  }, [filteredProviders, selectedProvider]);

  useEffect(() => {
    if (normalizedCustomGames.length > 0) changeOrderedGames(normalizedCustomGames);
  }, [changeOrderedGames, normalizedCustomGames]);

  return {
    input: {
      selectGameType,
      selectProvider,
      changeOrderedGames,
      setSearchString,
      handleSendSearchResult,
    },
    output: {
      games: filteredGames,
      total,
      hasGamesBeenChanged,
      orderedGames,
      providers,
      filteredProviders,
      disabledProviders,
      gameTypes,
      selectedGameType,
      selectedProvider,
      allPositionedGames,
      previousGames: normalizedPreviousGames,
      searchString,
    },
  };
};
